import React, { useState, useEffect } from 'react';
import { View, Text, FlatList, TouchableOpacity, TextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useAuth } from '@/providers/AuthProvider';
import { useThemeColors } from '@/providers/ThemeProvider';
import { supabase } from '@/lib/supabase/client';
import { Loader } from '@/components/Loader';

interface UserProfile {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

/**
 * Avatar component with fallback for failed image loads
 */
const UserAvatar = ({ user, size = 48 }: { user: UserProfile; size?: number }) => {
  const [imageError, setImageError] = useState(false);
  
  const initials = user.full_name.charAt(0).toUpperCase();
  
  if (!user.avatar_url || imageError) {
    return (
      <View 
        style={{ width: size, height: size }}
        className="rounded-full bg-blue-500 items-center justify-center"
      >
        <Text className="text-white font-bold text-lg">
          {initials}
        </Text>
      </View>
    );
  }
  
  return (
    <Image
      source={{ uri: user.avatar_url }}
      style={{ width: size, height: size, borderRadius: size / 2 }}
      contentFit="cover"
      onError={() => setImageError(true)}
    />
  );
};

export default function NewDirectChatScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user, activeProfile } = useAuth();
  const userId = activeProfile?.id || user?.id;

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = users.filter((u) =>
        u.full_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .neq('id', userId)
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
      setFilteredUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (selectedUser: UserProfile) => {
    if (!userId || creating) return;

    setCreating(true);
    try {
      // Use the find_or_create_direct_chat function
      const { data, error } = await supabase.rpc('find_or_create_direct_chat', {
        p_user1_id: userId,
        p_user2_id: selectedUser.id,
      });

      if (error) throw error;

      // Navigate to the chat
      router.replace(`/chat-screens/${data}` as any);
    } catch (err) {
      console.error('Error creating direct chat:', err);
      setCreating(false);
    }
  };

  const renderUser = ({ item }: { item: UserProfile }) => (
    <TouchableOpacity
      onPress={() => handleSelectUser(item)}
      disabled={creating}
      className="flex-row items-center px-4 py-3"
      style={{ backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border }}
      accessibilityRole="button"
      accessibilityLabel={`Start chat with ${item.full_name}`}
    >
      <UserAvatar user={item} size={48} />
      <Text className="ml-3 text-base font-medium flex-1" style={{ color: colors.text }}>
        {item.full_name}
      </Text>
      <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <SafeAreaView edges={['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center">
          <Loader size={60} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView edges={['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View className="px-4 py-3" style={{ backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border }}>
        <View className="flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="p-2 -ml-2"
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={24} color={colors.primary} />
          </TouchableOpacity>
          <Text className="ml-2 text-xl font-bold" style={{ color: colors.text }}>
            Nieuwe chat
          </Text>
        </View>
      </View>

      {/* Search */}
      <View className="px-4 py-3" style={{ backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border }}>
        <View className="flex-row items-center rounded-lg px-3 py-2" style={{ backgroundColor: colors.surfaceSecondary }}>
          <Ionicons name="search" size={20} color={colors.textTertiary} />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Zoek gebruikers..."
            placeholderTextColor={colors.textTertiary}
            className="flex-1 ml-2"
            style={{ color: colors.text }}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Creating overlay */}
      {creating && (
        <View className="absolute inset-0 bg-black/50 z-10 items-center justify-center">
          <View className="rounded-lg p-6 items-center" style={{ backgroundColor: colors.surface }}>
            <Loader size={40} />
            <Text className="mt-3" style={{ color: colors.textSecondary }}>
              Chat starten...
            </Text>
          </View>
        </View>
      )}

      {/* User list */}
      <FlatList
        data={filteredUsers}
        keyExtractor={(item) => item.id}
        renderItem={renderUser}
        ListEmptyComponent={
          <View className="flex-1 items-center justify-center py-12">
            <Text style={{ color: colors.textSecondary }}>
              {searchQuery ? 'Geen gebruikers gevonden' : 'Geen gebruikers beschikbaar'}
            </Text>
          </View>
        }
        contentContainerStyle={{ flexGrow: 1 }}
      />
    </SafeAreaView>
  );
}

